import React, { useEffect, useState } from "react";
import { api, ApiError } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function monthLabel(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

// Any screen that confirms or rejects a claim fires this, so every other
// screen showing the same payments (the dashboard card, the Notebook) can
// refresh itself without being wired to each other.
export const LEDGER_CHANGED_EVENT = "toletbook:ledger-changed";
export function announceLedgerChanged() {
  window.dispatchEvent(new Event(LEDGER_CHANGED_EVENT));
}

export interface PaymentClaim {
  entryId: string;
  tenancyId: string;
  tenantName: string;
  propertyLabel: string | null;
  month: string;
  amount: number;
  mode: string | null;
  reference: string | null;
  note: string | null;
  claimedAt: string | null;
}

// "Paid via UPI · Ref 412345678901 · gave it to the caretaker" — what the
// tenant told us about the payment, so the owner knows what to look for.
export function ClaimDetails({ mode, reference, note }: { mode: string | null; reference: string | null | undefined; note: string | null | undefined }) {
  const { t } = useLanguage();
  const modeKey = mode === "cash" ? "modeCash" : mode === "bank" ? "modeBank" : "modeUpi";
  return (
    <div className="sub" style={{ marginTop: 6 }}>
      <b>{t(`ownerClaims.${modeKey}`)}</b>
      {reference ? ` · ${t("ownerClaims.refLine", { ref: reference })}` : mode === "upi" || mode === "bank" ? ` · ${t("ownerClaims.noRef")}` : ""}
      {note ? ` · "${note}"` : ""}
    </div>
  );
}

// Reject button + the small inline form that asks for an optional reason
// (the tenant sees it). Calls onDone after the server accepts the rejection.
export function RejectClaim({ entryId, onDone }: { entryId: string; onDone?: () => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    try {
      await api.post(`/api/ledger/${entryId}/reject`, reason.trim() ? { reason: reason.trim() } : {});
      toast(t("ownerClaims.rejectedToast"));
      announceLedgerChanged();
      onDone?.();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setOpen(true)}>
        {t("ownerClaims.rejectBtn")}
      </button>
    );
  }
  return (
    <div style={{ flexBasis: "100%", marginTop: 4 }}>
      <div className="field" style={{ marginBottom: 8 }}>
        <label>{t("ownerClaims.reasonLabel")}</label>
        <div className="input-wrap">
          <input value={reason} maxLength={160} onChange={(e) => setReason(e.target.value)} placeholder={t("ownerClaims.reasonPlaceholder")} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={send}>
          {t("ownerClaims.sendReject")}
        </button>
        <button type="button" className="btn-outline btn-sm" disabled={busy} onClick={() => setOpen(false)}>
          ✕
        </button>
      </div>
    </div>
  );
}

// The dashboard card: every payment a tenant says they've made that the
// owner hasn't yet confirmed or rejected, oldest first. Renders nothing when
// there's nothing waiting.
export function ClaimsToConfirm() {
  const { t } = useLanguage();
  const toast = useToast();
  const [claims, setClaims] = useState<PaymentClaim[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    api.get("/api/ledger/awaiting-confirmation").then((d) => setClaims(d.claims)).catch(() => setClaims([]));
  }
  useEffect(() => {
    load();
    window.addEventListener(LEDGER_CHANGED_EVENT, load);
    return () => window.removeEventListener(LEDGER_CHANGED_EVENT, load);
  }, []);

  async function confirmClaim(c: PaymentClaim) {
    setBusyId(c.entryId);
    try {
      await api.patch(`/api/ledger/${c.entryId}`, { status: "paid" });
      toast(t("ownerClaims.confirmedToast"));
      announceLedgerChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusyId(null);
    }
  }

  if (!claims || claims.length === 0) return null;

  return (
    <div className="card claim-card" style={{ marginBottom: 16, borderColor: "var(--cash)" }}>
      <div className="card-row" style={{ marginBottom: 4 }}>
        <b>{t("ownerClaims.title")}</b>
        <span className="badge pending_confirmation">{claims.length}</span>
      </div>
      {claims.map((c) => (
        <div key={c.entryId} style={{ padding: "12px 0", borderTop: "1px dashed var(--line)" }}>
          <div className="card-row">
            <div style={{ minWidth: 0 }}>
              <b>{c.tenantName}</b>
              <div className="sub" style={{ fontSize: 12 }}>
                {c.propertyLabel ? `${c.propertyLabel} · ` : ""}
                {monthLabel(c.month)}
              </div>
            </div>
            <b>{fmtINR(c.amount)}</b>
          </div>
          <ClaimDetails mode={c.mode} reference={c.reference} note={c.note} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <button type="button" className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busyId === c.entryId} onClick={() => confirmClaim(c)}>
              {t("ownerClaims.confirmBtn")}
            </button>
            <RejectClaim entryId={c.entryId} />
          </div>
        </div>
      ))}
    </div>
  );
}
